import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload as UploadIcon, Image as ImageIcon, Camera, Loader2, PlayCircle, ArrowLeft, ShieldCheck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Checkbox } from "@/components/ui/checkbox";
import { analyzeLetter, createSample, saveOwnership } from "@/lib/api";

const MAX_MB = 15;

export default function Upload() {
  const nav = useNavigate();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (!/^image\//i.test(f.type)) {
      toast.error("Please choose a photo of the letter (JPG, PNG, WEBP).");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`That file is too large. Please keep it under ${MAX_MB} MB.`);
      return;
    }
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreview(url);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const submit = async () => {
    if (!file) { toast.error("Please add a photo of the letter first."); return; }
    if (!consent) { toast.error("Please confirm you understand LetterLifts gives general assistance."); return; }
    setBusy(true);
    try {
      const r = await analyzeLetter(file, true);
      saveOwnership(r.id, r.owner_token, r.share_token);
      toast.success("Letter read. Here's what it says.");
      nav(`/letter/${r.id}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "We couldn't read that letter. Please try a clearer photo.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const trySample = async () => {
    setBusy(true);
    try {
      const r = await createSample();
      saveOwnership(r.id, r.owner_token, r.share_token);
      nav(`/letter/${r.id}`);
    } catch {
      toast.error("Could not load the sample.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Header />
      <main data-testid="upload-page" className="mx-auto max-w-4xl px-6 py-12">
        <button
          onClick={() => nav("/")}
          className="mb-6 inline-flex items-center gap-2 text-base font-semibold text-moss-700 hover:underline"
          data-testid="back-to-landing"
        >
          <ArrowLeft strokeWidth={2.5} className="h-5 w-5" /> Back
        </button>

        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900">
          Upload a letter
        </h1>
        <p className="mt-4 text-xl leading-relaxed text-slate-700">
          Take a photo of the whole page, or pick a file. Good light and a flat surface help.
        </p>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`mt-10 rounded-3xl border-4 border-dashed p-10 sm:p-14 text-center transition-colors ${
            dragOver ? "border-moss-700 bg-moss-50" : "border-moss-700/30 bg-moss-50/40"
          }`}
          data-testid="upload-dropzone"
        >
          {preview ? (
            <div className="flex flex-col items-center gap-6">
              <img
                src={preview}
                alt="Selected letter"
                className="max-h-[380px] rounded-2xl border-2 border-slate-200 bg-white object-contain"
                data-testid="upload-preview"
              />
              <button
                onClick={() => { setFile(null); setPreview(null); }}
                className="text-base font-semibold text-slate-600 underline hover:text-slate-900"
                data-testid="upload-clear"
              >
                Choose a different photo
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-5">
              <span className="grid h-16 w-16 place-items-center rounded-2xl bg-moss-700 text-bone">
                <ImageIcon strokeWidth={2.5} className="h-8 w-8" />
              </span>
              <p className="text-2xl font-semibold text-slate-900">
                Drop the photo here — or pick one below
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={() => inputRef.current?.click()}
                  className="inline-flex items-center gap-2 rounded-full bg-moss-700 px-7 py-4 text-lg font-bold text-bone hover:bg-moss-800 min-h-[56px]"
                  data-testid="upload-choose-file"
                >
                  <UploadIcon strokeWidth={2.5} className="h-5 w-5" /> Choose a photo
                </button>
                <label
                  className="inline-flex items-center gap-2 rounded-full border-2 border-moss-700 bg-white px-6 py-4 text-lg font-bold text-moss-700 hover:bg-moss-50 min-h-[56px] cursor-pointer"
                  data-testid="upload-take-photo"
                >
                  <Camera strokeWidth={2.5} className="h-5 w-5" />
                  Take a photo
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    hidden
                    onChange={(e) => handleFile(e.target.files?.[0])}
                  />
                </label>
              </div>
              <p className="text-sm text-slate-500">JPG, PNG, or WEBP · up to {MAX_MB} MB</p>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="upload-file-input"
          />
        </div>

        {/* Consent */}
        <label className="mt-10 flex items-start gap-4 rounded-2xl border-2 border-slate-200 bg-white p-6 cursor-pointer">
          <Checkbox
            checked={consent}
            onCheckedChange={(v) => setConsent(Boolean(v))}
            className="mt-1 h-6 w-6 border-2 border-moss-700 data-[state=checked]:bg-moss-700 data-[state=checked]:text-bone"
            data-testid="consent-checkbox"
          />
          <span className="text-lg leading-relaxed text-slate-800">
            I understand LetterLifts provides <b>general document assistance</b> —
            not legal, medical, or financial advice. My letter will be read by an AI to help me understand it.
          </span>
        </label>

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <button
            onClick={submit}
            disabled={busy || !file || !consent}
            className="inline-flex items-center gap-3 rounded-full bg-moss-700 px-8 py-4 text-lg font-bold text-bone hover:bg-moss-800 disabled:opacity-50 min-h-[56px]"
            data-testid="analyze-letter-btn"
          >
            {busy ? (
              <><Loader2 strokeWidth={2.5} className="h-5 w-5 animate-spin" /> Reading your letter…</>
            ) : (
              <><ShieldCheck strokeWidth={2.5} className="h-5 w-5" /> Read this letter</>
            )}
          </button>
          <button
            onClick={trySample}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-full border-2 border-moss-700 bg-white px-6 py-4 text-lg font-bold text-moss-700 hover:bg-moss-50 min-h-[56px]"
            data-testid="upload-try-sample"
          >
            <PlayCircle strokeWidth={2.5} className="h-5 w-5" /> Or try a sample
          </button>
        </div>
      </main>
      <Footer />
    </>
  );
}
